/* eslint-disable prefer-const */
import _ from 'lodash';
import { WoodOutlinedColoredBeam } from './ModelElements/WoodOutlinedColoredBeam';
import FiberText from './FiberText/FiberText';
import getBeamModel from './getBeamModel';

function createLamellaData(Component, width, thickness, length, position, rotation, key, color, scaleMultiplier = 1, extraProps = null) {
  const safeScale = Number.isFinite(scaleMultiplier) && scaleMultiplier > 0 ? scaleMultiplier : 1;
  return {
    component: Component,
    scale: [(width / 200) * safeScale, (thickness / 10000) * safeScale, (length / 5000) * safeScale],
    position,
    rotation,
    key,
    color,
    ...(extraProps || {})
  };
}

function getQualityText(index, beamBuild) {
  return beamBuild[index] || 'ni';
}

function resolveColorFromEntry(entry, beamColors, beamBuildText, qualityColorIndexMap = null) {
  const safeColors = Array.isArray(beamColors) && beamColors.length ? beamColors : ['#808080'];
  const normalizeToken = (val) =>
    String(val || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  const normalizedBuildText = (Array.isArray(beamBuildText) ? beamBuildText : []).map((t) => String(t).toLowerCase());
  const sanitizedBuildText = (Array.isArray(beamBuildText) ? beamBuildText : []).map((t) => normalizeToken(t));
  let idx = -1;
  if (Number.isFinite(entry)) {
    idx = entry;
  } else if (typeof entry === 'string') {
    const normalizedEntry = entry.toLowerCase();
    idx = normalizedBuildText.indexOf(normalizedEntry);
    if (idx < 0) {
      const sanitizedEntry = normalizeToken(entry);
      idx = sanitizedBuildText.indexOf(sanitizedEntry);
    }
    if (idx < 0 && qualityColorIndexMap) {
      if (qualityColorIndexMap[normalizedEntry] !== undefined) {
        idx = qualityColorIndexMap[normalizedEntry];
      } else {
        const nextIdx = Object.keys(qualityColorIndexMap).length % safeColors.length;
        qualityColorIndexMap[normalizedEntry] = nextIdx;
        idx = nextIdx;
      }
    }
  }
  if (idx < 0) idx = 0;
  return getColorFromString(idx, safeColors);
}

function getDisplayText(entry, beamBuildText) {
  if (Number.isFinite(entry)) return getQualityText(entry, beamBuildText);
  if (entry === null || entry === undefined) return 'ni';
  return String(entry);
}

function getBeamSequence(bshlayer, beamBuild) {
  if (!Array.isArray(beamBuild)) {
    throw new Error('beamBuild should be an array');
  }

  const { numOfLamellas } = bshlayer;
  const sequence = [];
  const order = beamBuild.map((layer) => {
    if (bshlayer[layer] === undefined) {
      throw new Error(`Property ${layer} is not defined in bshlayer`);
    }
    return bshlayer[layer];
  });

  const total = order.reduce((acc, val) => acc + val, 0);
  let perLamella = Math.floor(numOfLamellas / total);
  let remainder = numOfLamellas % total;

  for (let i = 0; i < order.length; i++) {
    let count = order[i] * perLamella;
    if (remainder > 0) {
      count += 1;
      remainder -= 1;
    }
    for (let j = 0; j < count; j++) {
      sequence.push(i);
    }
  }
  // console.log('sequence: ', sequence);
  return sequence;
}

function getColorFromString(index, colors) {
  const safeColors = Array.isArray(colors) && colors.length ? colors : ['#808080'];
  const normalizedIndex = Number.isFinite(index) ? index : 0;
  const raw = safeColors[normalizedIndex % safeColors.length];
  const color = typeof raw === 'string' ? raw : '#808080';
  const trimmed = color.startsWith('#') ? color.slice(1) : color;
  return trimmed || '808080'; // Return color without #
}

function resolveLamellaList(layer, order) {
  const sortLamellas = (list) => list.slice().sort((a, b) => (Number(a?.lamellaNo) || 0) - (Number(b?.lamellaNo) || 0));
  if (Array.isArray(layer?.lamellas) && layer.lamellas.length) {
    return sortLamellas(layer.lamellas);
  }

  if (Array.isArray(order?.lamellaSequence) && order.lamellaSequence.length) {
    return sortLamellas(order.lamellaSequence);
  }

  const fallbackLayers = Array.isArray(order?.layers) ? order.layers : Array.isArray(order?.bshLayers) ? order.bshLayers : [];

  for (const fallbackLayer of fallbackLayers) {
    if (Array.isArray(fallbackLayer?.lamellas) && fallbackLayer.lamellas.length) {
      return sortLamellas(fallbackLayer.lamellas);
    }
  }

  return [];
}

function computeAutoBundleLayout(targetSize, itemCount, baseItemSize, baseGap = 0) {
  const count = Math.max(1, Number(itemCount) || 1);
  const safeBaseItem = Number.isFinite(baseItemSize) && baseItemSize > 0 ? baseItemSize : 0.0001;
  const safeBaseGap = Number.isFinite(baseGap) && baseGap > 0 ? baseGap : 0;
  const fallbackTotal = safeBaseItem * count + safeBaseGap * Math.max(count - 1, 0);
  const total = Number.isFinite(targetSize) && targetSize > 0 ? targetSize : fallbackTotal;

  const ratio = safeBaseItem > 0 ? Math.max(0, safeBaseGap / safeBaseItem) : 0;
  let itemSize = total / (count + Math.max(count - 1, 0) * ratio);
  if (!Number.isFinite(itemSize) || itemSize <= 0) {
    itemSize = total / count;
  }
  itemSize = Math.max(itemSize, 0.0001);

  const gap = count > 1 ? Math.max(total - itemSize * count, 0) / (count - 1) : 0;
  return { itemSize, gap };
}

function computePackedCenters(totalSpan, itemSpan, itemCount, gapSpan = 0) {
  const count = Math.max(1, Number(itemCount) || 1);
  const safeItemSpan = Number.isFinite(itemSpan) && itemSpan > 0 ? itemSpan : 0.0001;
  const safeGapSpan = Number.isFinite(gapSpan) && gapSpan > 0 ? gapSpan : 0;
  const resolvedTotal = Number.isFinite(totalSpan) && totalSpan > 0 ? totalSpan : safeItemSpan * count + safeGapSpan * Math.max(count - 1, 0);

  const centers = [];
  let cursor = -resolvedTotal / 2;
  for (let index = 0; index < count; index++) {
    const center = cursor + safeItemSpan / 2;
    centers.push(center);
    cursor += safeItemSpan + safeGapSpan;
  }
  return centers;
}

export default function getBeamSingleModel(order, layer, id, beamOptions) {
  const lamellas = [];
  const beamLayer = layer;

  /* Beam options from config */
  const beamBuild = _.get(beamOptions, 'beamBuild', null);
  const beamBuildText = _.get(beamOptions, 'beamBuildText', null);
  const beamColors = _.get(beamOptions, 'beamColors', null);
  const usePerLamellaPalette = _.get(beamOptions, 'beamColorMode', '') === 'perLamellaPalette' && Array.isArray(beamColors) && beamColors.length > 1;
  const lamellaColorPaletteProps = usePerLamellaPalette ? { colorPalette: beamColors } : null;
  // const textColors = _.get(beamOptions, 'textColors', null);
  const renderMode = _.get(beamOptions, 'renderMode', 'legacy');
  // const alignPointStart = 0; // Replace 0 with the actual Y-axis (or Z-axis) position of the green line.
  const widthScaleDivRaw = Number(_.get(beamOptions, 'beamSingleWidthScaleDiv', _.get(beamOptions, 'widthScaleDiv', 10)));
  const widthScaleDiv = Number.isFinite(widthScaleDivRaw) && widthScaleDivRaw > 0 ? widthScaleDivRaw : 10;
  const thicknessScaleMultiplierRaw = Number(_.get(beamOptions, 'thicknessScaleMultiplier', 5));
  const thicknessScaleMultiplier = Number.isFinite(thicknessScaleMultiplierRaw) ? thicknessScaleMultiplierRaw : 5;
  const lengthScaleMultiplierRaw = Number(_.get(beamOptions, 'lengthScaleMultiplier', 5));
  const lengthScaleMultiplier = Number.isFinite(lengthScaleMultiplierRaw) ? lengthScaleMultiplierRaw : 5;
  const beamScaleRaw = Number(_.get(beamOptions, 'beamSingleScale', 2));
  const beamScale = Number.isFinite(beamScaleRaw) && beamScaleRaw > 0 ? beamScaleRaw : 1;
  const beamSingleGapRaw = Number(_.get(beamOptions, 'beamSingleGap', _.get(beamOptions, 'beamSingleGapRaw', Number.NaN)));
  const beamSingleEnableRotation = _.get(beamOptions, 'beamSingleEnableRotation', _.get(beamOptions, 'beamSingleRotationEnabled', undefined));
  const beamSingleRotation = _.get(beamOptions, 'beamSingleRotation', _.get(beamOptions, 'beamSingleRotationAngle', undefined));
  const beamSingleGlued = _.get(beamOptions, 'beamSingleGlued', false) && renderMode === 'enhanced';
  const renderQualityText = _.get(beamOptions, 'renderQualityText', true);
  const stackDirection = _.get(beamOptions, 'stackDirection', 'vertical');
  const bundleSizeRaw = _.get(beamOptions, 'bundleSize', null);
  const bundleSize = Number.isFinite(Number(bundleSizeRaw)) && Number(bundleSizeRaw) > 0 ? Number(bundleSizeRaw) : null;
  const isHorizontal = stackDirection === 'horizontal';

  const lamellaList = resolveLamellaList(beamLayer, order);
  const pickNumber = (...vals) => {
    for (const val of vals) {
      const num = Number(val);
      if (Number.isFinite(num) && num > 0) return num;
    }
    return null;
  };
  const sampleLamella = lamellaList[0] || {};
  const actualLamellaWidth = pickNumber(beamLayer?.lamellaWidth, sampleLamella?.lamellaWidth, sampleLamella?.width);
  const actualLamellaThickness = pickNumber(beamLayer?.lamellaThickness, sampleLamella?.lamellaThickness, sampleLamella?.thickness);
  const actualLamellaLength = pickNumber(beamLayer?.lamellaLength, sampleLamella?.lamellaLength, sampleLamella?.length);
  const fixedDims = _.get(beamOptions, 'fixedDimensions', _.get(beamOptions, 'beamSingleFixedDimensions', null));
  const fixedLamellaWidth = pickNumber(_.get(beamOptions, 'fixedLamellaWidth', null), _.get(beamOptions, 'beamSingleFixedWidth', null), fixedDims?.width, fixedDims?.lamellaWidth, fixedDims?.w);
  const fixedLamellaThickness = pickNumber(
    _.get(beamOptions, 'fixedLamellaThickness', null),
    _.get(beamOptions, 'beamSingleFixedThickness', null),
    fixedDims?.thickness,
    fixedDims?.lamellaThickness,
    fixedDims?.t,
    fixedDims?.h
  );
  const fixedLamellaLength = pickNumber(_.get(beamOptions, 'fixedLamellaLength', null), _.get(beamOptions, 'beamSingleFixedLength', null), fixedDims?.length, fixedDims?.lamellaLength, fixedDims?.l);
  const lamellaWidthValue = fixedLamellaWidth ?? actualLamellaWidth;
  const lamellaThicknessValue = fixedLamellaThickness ?? actualLamellaThickness;
  const lamellaLengthValue = fixedLamellaLength ?? actualLamellaLength;
  const numLamellasRaw = Number(beamLayer?.numOfLamellas);
  const resolvedNumLamellas = Number.isFinite(numLamellasRaw) && numLamellasRaw > 0 ? numLamellasRaw : lamellaList.length;
  const resolvedBeamLayer =
    beamLayer && (lamellaWidthValue || lamellaThicknessValue || lamellaLengthValue || resolvedNumLamellas)
      ? {
          ...beamLayer,
          lamellaWidth: lamellaWidthValue ?? beamLayer.lamellaWidth,
          lamellaThickness: lamellaThicknessValue ?? beamLayer.lamellaThickness,
          lamellaLength: lamellaLengthValue ?? beamLayer.lamellaLength,
          numOfLamellas: resolvedNumLamellas || beamLayer.numOfLamellas
        }
      : beamLayer;

  const qualityColorIndexMap = lamellaList.length ? {} : null;

  if (renderMode === 'enhanced') {
    const enhancedOptions = Number.isFinite(beamSingleGapRaw) && beamSingleGapRaw > 0 ? { ...beamOptions, orderVerticalGap: beamSingleGapRaw } : { ...beamOptions };

    if (beamSingleEnableRotation !== undefined) {
      enhancedOptions.enableRotation = beamSingleEnableRotation;
    }
    if (beamSingleRotation !== undefined) {
      enhancedOptions.rotationAngle = beamSingleRotation;
    }

    if (beamSingleGlued && beamLayer) {
      enhancedOptions.orderVerticalGap = (((lamellaThicknessValue || 0) * beamScale) / 10000) * thicknessScaleMultiplier; // hopefully dela vredi za razlicne velikosti
      enhancedOptions.useNormalizedLayerSpacing = false;
    }

    if (lamellaList.length) {
      const uniqueQualities = [];
      const uniqueMap = {};
      const counts = [];
      const lamellaSequence = [];
      const pendingSequence = [];

      lamellaList.forEach((lam) => {
        const display = lam.quality || lam.grade || lam.strengthString || 'ni';
        const key = String(display).toLowerCase();
        if (uniqueMap[key] === undefined) {
          uniqueMap[key] = uniqueQualities.length;
          uniqueQualities.push(display);
          counts.push(1);
        } else {
          counts[uniqueMap[key]] += 1;
        }
        lamellaSequence.push(uniqueMap[key]);
        pendingSequence.push(lam?.pending === true);
      });

      enhancedOptions.beamBuildText = uniqueQualities;
      enhancedOptions.beamBuild = counts;
      enhancedOptions.lamellaSequence = lamellaSequence;
      enhancedOptions.pendingSequence = pendingSequence;
      enhancedOptions.useNormalizedLayerSpacing = false;
    }

    const enhancedLamellas = getBeamModel(order, resolvedBeamLayer || layer, id, enhancedOptions);
    // Keep bundle dimensions exact: do not apply extra single-scale after bundle layout is computed.
    const shouldApplySingleScale = beamScale !== 1 && bundleSize === null;
    if (!Array.isArray(enhancedLamellas) || !shouldApplySingleScale) return enhancedLamellas;
    return enhancedLamellas.map((item) => {
      const scale = Array.isArray(item.scale) ? item.scale.map((val) => val * beamScale) : item.scale;
      return { ...item, scale };
    });
  }

  /* Selected layer */
  if (resolvedBeamLayer) {
    let beamSequence = [];
    try {
      beamSequence = getBeamSequence(resolvedBeamLayer, beamBuild);
    } catch (e) {
      beamSequence = [];
    }
    if (lamellaList.length > 0) {
      beamSequence = lamellaList.map((lam) => lam.quality || lam.grade || lam.strengthString || '');
    }

    let lamellaBuild = [];
    if (Number.isFinite(lamellaWidthValue) && lamellaWidthValue > 0) {
      lamellaBuild = [lamellaWidthValue / widthScaleDiv]; // Extract widths from widthoffset
    }

    if (!Array.isArray(beamSequence) || beamSequence.length === 0) {
      const fallbackCount = resolvedNumLamellas && resolvedNumLamellas > 0 ? resolvedNumLamellas : lamellaBuild.length || lamellaList.length || 1;
      beamSequence = Array(fallbackCount).fill(0);
    }

    if (lamellaBuild.length === 0) {
      lamellaBuild = [1]; // minimal width fallback to avoid empty/NaN geometry
    }

    const lamellaLengthMm = actualLamellaLength ?? lamellaLengthValue;
    const lamellaLengthScale = Number.isFinite(lamellaLengthValue) && lamellaLengthValue > 0 ? lamellaLengthValue * lengthScaleMultiplier : 1;
    const lamellaLengthNormalized = Number.isFinite(lamellaLengthValue) && lamellaLengthValue > 0 ? (1 - lamellaLengthValue / 14650) / 2 : 0;
    const totalWidth = lamellaBuild.reduce((partialSum, w) => partialSum + w, 0) || 1;
    const seqLength = beamSequence.length || 1;
    let effectiveLamellaWidth = lamellaBuild[0] || 1;
    const baseThicknessScale = (lamellaThicknessValue || 0) * thicknessScaleMultiplier;
    let effectiveThicknessScale = baseThicknessScale;
    let autoHorizontalGap = 0;
    let autoVerticalGap = Number.isFinite(beamSingleGapRaw) && beamSingleGapRaw > 0 ? beamSingleGapRaw : 0;

    if (bundleSize !== null) {
      if (isHorizontal) {
        const safeBeamScale = Number.isFinite(beamScale) && beamScale > 0 ? beamScale : 1;
        // Horizontal bundling must fit exact final rendered width, independent of beamScale.
        const bundleSizeScene = bundleSize / widthScaleDiv / safeBeamScale;
        const fixedBundleWidthScene = Number.isFinite(fixedLamellaWidth) && fixedLamellaWidth > 0 ? fixedLamellaWidth / widthScaleDiv / safeBeamScale : null;
        if (fixedBundleWidthScene && fixedBundleWidthScene > 0) {
          effectiveLamellaWidth = fixedBundleWidthScene;
          autoHorizontalGap = seqLength > 1 ? Math.max((bundleSizeScene - fixedBundleWidthScene * seqLength) / (seqLength - 1), 0) : 0;
        } else {
          const autoLayout = computeAutoBundleLayout(bundleSizeScene, seqLength, effectiveLamellaWidth, 0);
          effectiveLamellaWidth = Math.max(autoLayout.itemSize, 0.01);
          autoHorizontalGap = autoLayout.gap;
        }
      } else {
        const bundleSizeScene = (bundleSize * thicknessScaleMultiplier) / 10000;
        const safeBeamScale = Number.isFinite(beamScale) && beamScale > 0 ? beamScale : 1;
        const autoLayout = computeAutoBundleLayout(bundleSizeScene / safeBeamScale, seqLength, baseThicknessScale / 10000, 0);
        effectiveThicknessScale = Math.max(autoLayout.itemSize * 10000, 0.1);
        autoVerticalGap = 0;
      }
    }

    const lamellaHeightScene = (effectiveThicknessScale / 10000) * beamScale;
    const activeVerticalGap = bundleSize !== null && !isHorizontal ? autoVerticalGap : beamSingleGapRaw;
    const hasActiveVerticalGap = Number.isFinite(activeVerticalGap) && activeVerticalGap > 0;
    const layerSpacing = beamSingleGlued ? lamellaHeightScene + (hasActiveVerticalGap ? activeVerticalGap : 0) : hasActiveVerticalGap ? activeVerticalGap : 1 / seqLength;
    let n = 1;
    for (let nIndex = 0; nIndex < n; nIndex++) {
      if (lamellaBuild.length > 0) {
        const initialOffset = -totalWidth / 2;
        let currentHeight = 0;
        const horizontalGap = bundleSize !== null ? autoHorizontalGap : 0;
        const lamellaWidthWorld = (effectiveLamellaWidth * beamScale) / 100;
        const horizontalGapWorld = (horizontalGap * beamScale) / 100;
        const totalHorizontalSpanWorld = seqLength * lamellaWidthWorld + Math.max(seqLength - 1, 0) * horizontalGapWorld;
        const horizontalCenters = computePackedCenters(totalHorizontalSpanWorld, lamellaWidthWorld, seqLength, horizontalGapWorld);

        for (let j = 0; j < beamSequence.length; j++) {
          const isPendingLamella = lamellaList?.[j]?.pending === true || String(beamSequence[j] ?? '').toLowerCase() === 'pending';
          const lamellaRenderProps = {
            ...(lamellaColorPaletteProps || {}),
            ...(isPendingLamella ? { desaturate: true, saturationMultiplier: 0.28 } : {})
          };
          if (isHorizontal) {
            const currentText = getDisplayText(beamSequence[j], beamBuildText);
            const xPosition = (horizontalCenters[j] ?? 0) + nIndex;
            lamellas.push(
              createLamellaData(
                WoodOutlinedColoredBeam,
                effectiveLamellaWidth,
                effectiveThicknessScale,
                lamellaLengthScale,
                [xPosition, 0, 32 * lamellaLengthNormalized],
                0,
                `WoodOutlined_${nIndex}_${j}_0`,
                resolveColorFromEntry(beamSequence[j], beamColors, beamBuildText, qualityColorIndexMap),
                beamScale,
                lamellaRenderProps
              )
            );

            if (renderQualityText) {
              lamellas.push({
                component: FiberText,
                position: [xPosition, 0.2, 5],
                children: currentText,
                size: 0.8,
                key: `text-${nIndex}-${j}-0`,
                color: `#${resolveColorFromEntry(beamSequence[j], beamColors, beamBuildText, qualityColorIndexMap)}`
              });
            }

            if (renderQualityText && j === beamSequence.length - 1 && lamellaLengthMm) {
              lamellas.push({
                component: FiberText,
                position: [xPosition, 0.4, 32 * lamellaLengthNormalized - 4],
                children: `${lamellaLengthMm} mm`,
                size: 1.5,
                key: `length-text-${nIndex}-${j}`,
                color: '#000000',
                billboard: true
              });
            }
            continue;
          }

          let offset = -totalWidth / 2;
          let reversed = (j + 1) % 2 === 1 ? lamellaBuild.slice().reverse() : lamellaBuild;
          const groupStartPosition = beamSingleGlued ? currentHeight : j * layerSpacing;
          const localHeight = beamSingleGlued ? lamellaHeightScene : layerSpacing;
          const groupEndPosition = groupStartPosition + localHeight;
          const textPosition = beamSingleGlued ? groupStartPosition + localHeight / 2 : (groupStartPosition + groupEndPosition) / 2;

          for (let i = 0; i < reversed.length; i++) {
            offset += reversed[i];
            const currentText = getDisplayText(beamSequence[j], beamBuildText);

            lamellas.push(
              createLamellaData(
                WoodOutlinedColoredBeam,
                reversed[i],
                effectiveThicknessScale,
                lamellaLengthScale,
                [offset / 100 + nIndex, beamSingleGlued ? groupStartPosition + localHeight / 2 : groupStartPosition, 32 * lamellaLengthNormalized],
                0,
                `WoodOutlined_${nIndex}_${j}_${i}`,
                resolveColorFromEntry(beamSequence[j], beamColors, beamBuildText, qualityColorIndexMap),
                beamScale,
                lamellaRenderProps
              )
            );

            const isLastItem = j === beamSequence.length - 1 && i == reversed.length - 1;

            if (renderQualityText) {
              lamellas.push({
                component: FiberText,
                position: [initialOffset / 200 + 0.3 + nIndex * 2, textPosition, 5],
                children: currentText,
                size: 0.8,
                key: `text-${nIndex}-${j}-${i}`,
                color: `#${resolveColorFromEntry(beamSequence[j], beamColors, beamBuildText, qualityColorIndexMap)}`
              });
            }

            // Add lamella length text on top of the stack
            if (renderQualityText && isLastItem && lamellaLengthMm) {
              lamellas.push({
                component: FiberText,
                position: [
                  offset / 100 + nIndex, // Centered on the lamella
                  groupEndPosition + 0.2, // Slightly above the lamella stack
                  32 * lamellaLengthNormalized - 4 // Match the lamella's position
                ],
                children: `${lamellaLengthMm} mm`, // Display the lamella length
                size: 1.5,
                key: `length-text-${nIndex}-${j}`,
                color: '#000000', // Black color for visibility
                billboard: true // Ensures the text always faces the camera
              });
            }

            offset += reversed[i];
          }

          if (beamSingleGlued) {
            currentHeight += localHeight;
            if (hasActiveVerticalGap) {
              currentHeight += activeVerticalGap;
            }
          }
        }
      }
    }
  }

  return lamellas;
}
